import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import api from '../lib/api';

const NotificationContext = createContext(null);
export const useNotifications = () => useContext(NotificationContext);

const POLL_MS = 20_000; // 20s
const PERM_DECLINED_KEY = 'midgard_push_declined';

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [pushPermission, setPushPermission] = useState('default');
  const prevUnread = useRef(0);
  const swReg = useRef(null);

  /* ---- fetch helpers ---- */
  const fetchUnreadCount = useCallback(async () => {
    if (!user) return;
    try {
      const res = await api.get('/notifications/unread-count');
      const count = res.data?.count ?? 0;

      // Browser notification for NEW unreads (only when tab is not focused)
      if (count > prevUnread.current && prevUnread.current >= 0 && document.hidden) {
        try {
          if (Notification.permission === 'granted') {
            new Notification('Midgard', {
              body: `You have ${count} unread notification${count > 1 ? 's' : ''}`,
              icon: '/icon-192.png',
              tag: 'midgard-unread',
            });
          }
        } catch (_) { /* not available */ }
      }
      prevUnread.current = count;
      setUnreadCount(count);
    } catch (_) { /* silent */ }
  }, [user]);

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    try {
      const res = await api.get('/notifications?limit=30');
      const list = res.data || [];
      setNotifications(list);
    } catch (_) { /* silent */ }
  }, [user]);

  /* ---- mark read ---- */
  const markRead = useCallback(async (id) => {
    try {
      await api.put(`/notifications/${id}/read`);
      setNotifications((prev) => prev.map((n) => n.id === id ? { ...n, is_read: true } : n));
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch (_) { /* silent */ }
  }, []);

  const markAllRead = useCallback(async () => {
    try {
      await api.put('/notifications/read');
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (_) { /* silent */ }
  }, []);

  /* ---- service worker + push subscription ---- */
  const registerPush = useCallback(async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    try {
      const reg = await navigator.serviceWorker.register('/sw-notifications.js');
      swReg.current = reg;

      // Get VAPID public key
      const keyRes = await api.get('/push/vapid-key');
      const vapidKey = keyRes.data?.public_key;
      if (!vapidKey) return;

      // Convert VAPID key to Uint8Array
      const padding = '='.repeat((4 - (vapidKey.length % 4)) % 4);
      const raw = atob(vapidKey.replace(/-/g, '+').replace(/_/g, '/') + padding);
      const arr = new Uint8Array(raw.length);
      for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);

      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: arr,
      });

      // Send subscription to backend
      await api.post('/push/subscribe', { subscription: sub.toJSON() });
    } catch (e) {
      console.warn('Push registration failed:', e.message);
    }
  }, []);

  const requestPushPermission = useCallback(async () => {
    if (!('Notification' in window)) return false;
    const result = await Notification.requestPermission();
    setPushPermission(result);
    if (result === 'granted') {
      localStorage.removeItem(PERM_DECLINED_KEY);
      await registerPush();
      return true;
    }
    if (result === 'denied') {
      localStorage.setItem(PERM_DECLINED_KEY, '1');
    }
    return false;
  }, [registerPush]);

  /* ---- init: poll + SW ---- */
  useEffect(() => {
    if (!user) {
      setNotifications([]);
      setUnreadCount(0);
      prevUnread.current = 0;
      return;
    }

    // Initial fetch
    fetchUnreadCount();
    fetchNotifications();

    // Polling
    const interval = setInterval(() => {
      fetchUnreadCount();
    }, POLL_MS);

    // Check push permission state
    if ('Notification' in window) {
      setPushPermission(Notification.permission);
      if (Notification.permission === 'granted') {
        registerPush();
      }
    }

    // Listen for SW deep-link messages
    const onMessage = (e) => {
      if (e.data?.type === 'NOTIFICATION_CLICK' && e.data.url) {
        window.location.href = e.data.url;
      }
    };
    navigator.serviceWorker?.addEventListener('message', onMessage);

    return () => {
      clearInterval(interval);
      navigator.serviceWorker?.removeEventListener('message', onMessage);
    };
  }, [user, fetchUnreadCount, fetchNotifications, registerPush]);

  /* ---- refresh helper for pages ---- */
  const refresh = useCallback(() => {
    fetchUnreadCount();
    fetchNotifications();
  }, [fetchUnreadCount, fetchNotifications]);

  const shouldShowPermissionPrompt =
    user &&
    pushPermission === 'default' &&
    !localStorage.getItem(PERM_DECLINED_KEY);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        markRead,
        markAllRead,
        refresh,
        pushPermission,
        requestPushPermission,
        shouldShowPermissionPrompt,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};
